// src/pwa/InstallArt.tsx: the install prompt's pictures and words, shared by the sheet (InstallCard.tsx) and /install
// (InstallScreen.tsx); the owner's pick, direction B (docs/DECISIONS.md, "Install prompt"). HomeScreen is the result:
// the builder's icon on a slice of a home screen, over the tenant's wallpaper (brand.app.wallpaper, an idea's image,
// blurred). StepArt is each iPhone step's glyph drawn as Apple draws it, and stepLine the one sentence under it.
// Every picture is decoration (aria-hidden): the sentences carry the steps.
import type { ComponentChildren } from "preact";
import { appIconPaths } from "../../shared/pwa";
import type { PublicTenant } from "../../shared/tenant";
import type { IosGuide } from "./install";

export type InstallStep = 1 | 2 | 3;

/** The icon the family will see: the rendered 192 px app icon. */
export function appIcon(t: PublicTenant): string {
  return appIconPaths(t.slug).icon192;
}

/** The wallpaper behind the icon: the configured idea's image (800 px), or null for the brand's soft wash. */
export function wallpaperOf(t: PublicTenant): string | null {
  const id = t.brand.app?.wallpaper;
  return (id && t.ideas.find((i) => i.id === id)?.image?.srcSmall) || null;
}

/**
 * The sheet's one line. A ping is promised only once the app can send one (PWA_MODE push): until then installing
 * brings the board one tap closer, and that is what it says.
 */
export function pitchLine(t: PublicTenant, pings: boolean): string {
  return pings ? `Get a ping when the ${t.team.label} replies.` : "Your board, one tap away on your home screen.";
}

const SHARE = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <path d="M12 3v12" />
    <path d="m8 7 4-4 4 4" />
    <path d="M8.5 10H7a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-1.5" />
  </svg>
);
const ADD = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <rect x="3.5" y="3.5" width="17" height="17" rx="4" />
    <path d="M12 8v8M8 12h8" />
  </svg>
);
const ARROW = (
  <svg class="ins-arrow" viewBox="0 0 44 12" aria-hidden="true">
    <path d="M1 6h40M36 1.5 41.5 6 36 10.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
  </svg>
);
const Dots = () => (
  <span class="ins-dots">
    <i />
    <i />
    <i />
  </span>
);

/** Their home screen with the builder's icon on it; the badge only once the app can ping. */
export function HomeScreen(props: { t: PublicTenant; badge: boolean }) {
  const { t } = props;
  const wall = wallpaperOf(t);
  const tile = (k: number) => (
    <span class="ins-cell" key={k}>
      <span class="ins-tile" />
      <span class="ins-lbl" />
    </span>
  );
  return (
    <div class="ins-screen" aria-hidden="true">
      {wall ? <div class="ins-wall" style={{ backgroundImage: `url("${wall}")` }} /> : null}
      <div class="ins-grid">
        {[0, 1, 2, 3, 4].map(tile)}
        <span class="ins-cell ins-app">
          <img src={appIcon(t)} alt="" width={58} height={58} />
          <span>{t.brand.app?.shortName ?? t.name}</span>
          {props.badge ? <b class="ins-badge">1</b> : null}
        </span>
        {[6, 7, 8, 9, 10, 11].map(tile)}
      </div>
    </div>
  );
}

/** One step's glyph, where the picture was. */
export function StepArt(props: { step: InstallStep; guide: IosGuide }) {
  const { step, guide } = props;
  let art: ComponentChildren;
  if (step === 1) {
    art =
      guide === "safari-26" ? (
        <>
          <span class="ins-circle ins-ring">
            <Dots />
          </span>
          {ARROW}
          <span class="ins-circle">{SHARE}</span>
        </>
      ) : (
        <span class="ins-circle ins-ring">{SHARE}</span>
      );
  } else if (step === 2) {
    art = (
      <div class="ins-rows">
        <span class="ins-ghost" />
        <span class="ins-row">
          Add to Home Screen
          {ADD}
        </span>
        <span class="ins-ghost" />
      </div>
    );
  } else {
    art =
      guide === "safari-26" ? (
        <>
          <span class="ins-tog">
            <i class="ins-switch" />
            <span>Open as Web App</span>
          </span>
          {ARROW}
          <span class="ins-add">Add</span>
        </>
      ) : (
        <span class="ins-add">Add</span>
      );
  }
  return (
    <div class="ins-stage" aria-hidden="true">
      {art}
    </div>
  );
}

/** Safari's "•••" button, inside a sentence. */
function MoreKey() {
  return (
    <span class="ins-kd" role="img" aria-label="More">
      <i />
      <i />
      <i />
    </span>
  );
}

/**
 * The sentence under each step. Safari 26 and later keeps Share behind "•••" and asks "Open as Web App"; earlier
 * Safari has Share on its toolbar; Chrome and Edge on iOS each have their own Share button.
 */
export function stepLine(step: InstallStep, guide: IosGuide): ComponentChildren {
  if (step === 1) {
    if (guide === "safari-26") {
      return (
        <>
          Tap <MoreKey /> below, then Share.
        </>
      );
    }
    return guide === "safari" ? "Tap Share below." : "Tap your browser's Share button.";
  }
  if (step === 2) return "Scroll down, then tap Add to Home Screen.";
  return guide === "safari-26" ? "Keep Open as Web App on, then tap Add." : "Tap Add, top right.";
}

/** The steps' progress in the sheet's head. */
export function StepBars(props: { step: InstallStep }) {
  return (
    <span class="ins-bars" aria-hidden="true">
      {[1, 2, 3].map((n) => (
        <i key={n} class={n <= props.step ? "on" : undefined} />
      ))}
    </span>
  );
}
